import { CacheRequestOptions, HTTP_CACHE_REQUEST_OPTIONS } from './interceptors/caching-interceptor';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FileInfo } from '@progress/kendo-angular-upload';
import { AttachmentBus } from '../models/AttachmentBus';
import { Subject } from 'rxjs';
import { RefreshToken } from '../models/RefreshToken';
import { MessageType } from '../enums/MessageType';
import { Message } from '../models/message';
import { DbTable } from '../enums/DbTable';
import { environment } from '../../../../environments/environment';

@Injectable()
export class BaseService {
    protected _http: any;
    constructor(_http: HttpClient) {
        this._http = _http;
        this.rootUrl = environment.APIEndPoint;
        this.rootUrl_v2 = environment.APIEndPoint_v2;
        this.rootUrlCPConn = environment.APICEndPoint;
        this.rootUrlSignalR = environment.APIEndPointSignalR;
        this.baseUrlCPConn = this.rootUrlCPConn + "Document/";
    }
    rootUrl: string;
    rootUrl_v2: string;
    baseUrl: string;
    baseUrl_v2: string;
    rootUrlSignalR: string;
    rootUrlCPConn: string;
    baseUrlCPConn: string;

    messageSubject: Subject<Message> = new Subject<Message>();
    refreshSubject: Subject<RefreshToken> = new Subject<RefreshToken>();
    refresh(token: RefreshToken = null) {
        if (!token) {
            token = new RefreshToken();
            token.all = true;
        }
        this.refreshSubject.next(token);
    }
    sendMessage(message: Message) {
        this.messageSubject.next(message);
    }
    clear(message: Message = null) {
        if (!message) {
            message = new Message();
            message.refreshTarget = DbTable.Any;
            message.messageType = MessageType.clear;
        }
        this.sendMessage(message);
    }
    get(url: string): Observable<any> {
        let headers = this.getHeaders();
        return this._http.get(url, { headers: headers });
    }
    /**
     * This makes a GET call with the base headers and allows adding additional custom headers
     *
     * @param {string} url
     * @param {*} headers
     * @return {*}  {Observable<any>}
     * @memberof BaseService
     */
    getWithHeaders(url: string, headers: any): Observable<any> {
        let baseHeaders = this.getHeaders();
        for (let key in headers) {
            if (headers.hasOwnProperty(key)) {  // Ensure it doesn't inherit prototype properties
                baseHeaders = baseHeaders.set(key, headers[key]);
            }
        }
        return this._http.get(url, { headers: baseHeaders });
    }

    /**
     * Makes a GET call with a custom responseType
     *
     * @param {string} url
     * @param {string} responseType 'arraybuffer' | 'blob' | 'json' | 'text'
     * @return {*}  {Observable<any>}
     * @memberof BaseService
     */
    getWithResponseType(url: string, responseType: 'arraybuffer' | 'blob' | 'json' | 'text'): Observable<any> {
        let headers = this.getHeaders();
        return this._http.get(url, { headers: headers, responseType: responseType } as any);
    }

    post(url: string, model: any): Observable<any> {
        let headers = this.getHeaders();
        return this._http.post(url, model, { headers: headers });
    }

    put(url: string, id: any, model: any): Observable<any> {
        let headers = this.getHeaders();
        return this._http.put(url + id, model, { headers });
    }

    delete(url: string, id: any): Observable<any> {
        let headers = this.getHeaders();
        return this._http.delete(url + id, { headers });
    }
    saveLenderContact(url: string, model: any): Observable<any> {
        let headers = this.getHeaders();
        return this._http.put(url, model, { headers });
    }
    save(model: any): Observable<any> {
        let headers = this.getHeaders();
        let url_call: string = this.baseUrl + "Save";
        return this._http.put(url_call, model, { headers });
    }
    saveOrderAttachmentData(model: AttachmentBus): Observable<any> {
        let headers = this.getHeaders();
        let url = this.rootUrl + "Order/SaveOrderAttachmentData";
        return this._http.put(url , model, { headers });
    }
    getDocument(url_call: string): Observable<any> {
        return this.get(url_call); // this.get(url_call); //, { headers: headers });
    }
    uploadFiles(url: string, files: FileInfo[], data: AttachmentBus): Observable<any> {
        let dataStr: string = JSON.stringify(data);
        //we dont need to send the bytes if its filled out cause we are posting the file
        data.attachment = null;
        const formData = new FormData();
        for (const file of files) {
            formData.append('files',file.rawFile, file.name);
        }
        formData.append("attachmentData", dataStr);
        return this._http.put(url, formData);
    }
    protected getHeaders(): HttpHeaders {
        var headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('cp-app-source', environment.appCode)
            .set('Accept', 'application/json');
        return headers;
    }
    protected getHeadersUL(userId: string, locationId: string, other1Key: string = 'other1Key', other1Value: string = '', other2Key: string = 'other2Key', other2Value: string = ''): HttpHeaders {
        const baseHeaders = this.getHeadersU(userId, other1Key, other1Value, other2Key, other2Value);
        var headers = baseHeaders
            .append("locationid", (locationId ? locationId : ''));
        return headers;
    }
    protected getOptionsUL(userId: string, locationId: string, other1Key: string = 'other1Key', other1Value: string = '', other2Key: string = 'other2Key', other2Value: string = '') {
        let options = {
            headers: this.getHeadersUL(userId, locationId, other1Key, other1Value, other2Key, other2Value)
        };
        return options;
    }
    protected getHeadersU(userId: string, other1Key: string = 'other1Key', other1Value: string = '', other2Key: string = 'other2Key', other2Value: string = ''): HttpHeaders {
        const baseHeaders = this.getHeaders();
        var headers = baseHeaders
            .append("userid", userId)
            .append(other1Key, other1Value)
            .append(other2Key, other2Value);
        return headers;
    }
    print(id: any): Observable<any> {
        return null;
    }
    getDBDate(): Observable<any> {
        let headers = this.getHeaders();
        let url = this.rootUrl + "Account/GetDBDate";
        return this._http.get(url, { headers: headers });
    }
    //virtual method
    remove(id: any): Observable<any> {
        return null;
    }
    protected getOptionsU(userId: string, other1Key: string = 'other1Key', other1Value: string = '', other2Key: string = 'other2Key', other2Value: string = '') {
        let options = {
            headers: this.getHeadersU(userId, other1Key, other1Value, other2Key, other2Value)
        };
        return options;
    }

    createCacheContext(options: CacheRequestOptions): HttpContext {
        let ctx: HttpContext = new HttpContext();
        ctx.set<CacheRequestOptions | null>(HTTP_CACHE_REQUEST_OPTIONS, options);
        return ctx;
    }
}
